/**
 * ui_v2 AuthContext — Firebase-backed, minimal surface for Track B.
 *
 * Stays in ui_v2 (not in @ponpon/api-client) because React Context identity
 * and vi.mock test patterns don't survive cross-package sharing — see
 * docs/plans/ui_v2/track-A-extraction.md "What stays in console / per-app".
 *
 * Uses the shared `firebaseAuth` singleton from @ponpon/api-client so both
 * apps point at the same Firebase project + JWT.
 */
import {
  createContext, useContext, useEffect, useState, ReactNode, useCallback,
} from 'react'
import {
  onAuthStateChanged,
  signOut as fbSignOut,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  GoogleAuthProvider,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  type User as FirebaseUser,
} from 'firebase/auth'
import {
  firebaseAuth,
  setAuthTokenGetter,
  setCachedAuthToken,
  setUnauthorizedHandler,
  isMobileWeb,
} from '@ponpon/api-client'
import { identify, resetIdentity, track } from '../lib/analytics'

const EMAIL_LINK_STORAGE_KEY = 'ponpon.emailForSignIn'


interface AuthContextValue {
  session: { user: { id: string; email: string | null } } | null
  loading: boolean
  signInWithGoogle: () => Promise<void>
  sendSignInLink: (email: string) => Promise<void>
  /**
   * If the current URL is a Firebase email-link callback, finish the
   * sign-in. Returns true when sign-in happened. LoginPage calls this
   * on mount.
   */
  completeEmailLinkSignIn: () => Promise<boolean>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider')
  return ctx
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<FirebaseUser | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Local dev: don't send the Firebase token. The backend runs with
    // DEBUG=true and resolves anonymous requests to user_id="debug-user"
    // — and ALL local project data is stored under that path. If we
    // send a real Firebase token, the backend uses the Firebase UID
    // instead and reads from a non-existent path, so chat history /
    // YDoc rooms / projects appear empty after a refresh. Per CLAUDE.md:
    // "Local dev: <user_id> is debug-user when unauthenticated, or the
    // Firebase UID when logged in." We pin to debug-user locally.
    const isLocalDev =
      typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
    setAuthTokenGetter(async () => {
      if (isLocalDev) return null
      const u = firebaseAuth.currentUser
      return u ? await u.getIdToken() : null
    })
    setUnauthorizedHandler(() => {
      // Only redirect if Firebase actually says we're signed out — otherwise a
      // backend that's down / talking to a different Firebase project would
      // 401 every request and bounce us into a /dashboard <-> /login loop
      // for an already-signed-in user.
      if (firebaseAuth.currentUser) return
      if (window.location.pathname !== '/login') {
        window.location.assign('/login')
      }
    })
    // Pick up any pending sign-in result from a redirect flow
    // (signInWithRedirect on mobile Google, or the magic-link
    // completion). Fire-and-forget — onAuthStateChanged below resolves
    // setUser. Errors are surfaced via the auth state observer (or
    // ignored if the URL just doesn't carry a pending redirect, which
    // is the steady-state case).
    getRedirectResult(firebaseAuth).catch(() => { /* no-op */ })

    const unsub = onAuthStateChanged(firebaseAuth, async (next) => {
      setUser(next)
      setLoading(false)
      // The conversion. `identify` aliases the anonymous journey onto the real
      // uid — without it the pre- and post-sign-in events belong to two
      // different people and the funnel reports zero conversions forever.
      //
      // Gated on identify's return, not on `next` being truthy: this observer
      // also fires on every page load and token refresh for an already
      // signed-in user, and tracking those would report a fresh conversion
      // each time an existing user opened the site.
      if (next && identify(next.uid)) {
        const created = next.metadata?.creationTime
        const lastSignIn = next.metadata?.lastSignInTime
        track('signed_in', {
          method: next.providerData[0]?.providerId ?? 'unknown',
          // Firebase exposes no "is this a signup" flag; on the very first
          // sign-in these two timestamps describe the same event.
          is_new_user: Boolean(created && lastSignIn && created === lastSignIn),
        })
      }
      // Push the current token into the SYNCHRONOUS asset-URL cache so
      // ``getAssetUrl`` can append ``?token=<jwt>`` to <img src=…> URLs.
      // The async ``setAuthTokenGetter`` path covers REST fetches that
      // use the Authorization header; <img> tags can't carry auth
      // headers, so the asset endpoint reads the JWT from the query
      // string. Without this hop every image/video request 401s and
      // the canvas shows broken-image placeholders — see v1's
      // apps/console/src/contexts/AuthContext.tsx setCachedAuthToken
      // wiring this mirrors. Local dev (isLocalDev) stays null so the
      // backend resolves to the debug-user namespace; otherwise pull
      // the fresh JWT off the current Firebase user.
      if (isLocalDev || !next) {
        setCachedAuthToken(null)
        return
      }
      try {
        const token = await next.getIdToken()
        setCachedAuthToken(token)
      } catch {
        setCachedAuthToken(null)
      }
    })
    return unsub
  }, [])

  const signInWithGoogle = useCallback(async () => {
    const provider = new GoogleAuthProvider()
    if (isMobileWeb()) {
      // Full-page redirect to accounts.google.com → comes back to the
      // same origin; getRedirectResult() in the effect above picks up
      // the credential. No popup, so iOS Safari and in-app browsers
      // work.
      await signInWithRedirect(firebaseAuth, provider)
      return
    }
    await signInWithPopup(firebaseAuth, provider)
  }, [])

  const sendSignInLink = useCallback(async (email: string) => {
    await sendSignInLinkToEmail(firebaseAuth, email, {
      url: window.location.origin + '/login',
      handleCodeInApp: true,
    })
    // Stash so the completion handler on /login (running after the
    // user opens the email link, possibly on a *different* device or
    // tab) can fish it back out without re-prompting. Falls back to a
    // prompt if storage was cleared between request and completion —
    // see completeEmailLinkSignIn below.
    try {
      window.localStorage.setItem(EMAIL_LINK_STORAGE_KEY, email)
    } catch { /* private mode: completion will prompt */ }
  }, [])

  /**
   * Finish a magic-link sign-in if the current URL is a Firebase
   * email-link callback. Returns ``true`` if a sign-in happened (the
   * onAuthStateChanged effect will then redirect away from /login).
   *
   * Called from LoginPage on mount. Lives here (not in the page) so
   * the import surface for ``firebase/auth`` stays in one file and the
   * page just talks to ``useAuth()``.
   */
  const completeEmailLinkSignIn = useCallback(async (): Promise<boolean> => {
    if (typeof window === 'undefined') return false
    if (!isSignInWithEmailLink(firebaseAuth, window.location.href)) return false

    let email: string | null = null
    try {
      email = window.localStorage.getItem(EMAIL_LINK_STORAGE_KEY)
    } catch { /* private mode → prompt below */ }
    if (!email) {
      // Cross-device case: user requested the link on their laptop and
      // clicked it on their phone. Ask for the same address so Firebase
      // can verify the link belongs to it (a sign-in attempt with a
      // mismatched email throws and we surface the error).
      email = window.prompt('Confirm your email to finish signing in:') ?? null
    }
    if (!email) return false

    await signInWithEmailLink(firebaseAuth, email, window.location.href)
    try {
      window.localStorage.removeItem(EMAIL_LINK_STORAGE_KEY)
    } catch { /* no-op */ }
    return true
  }, [])

  const signOut = useCallback(async () => {
    await fbSignOut(firebaseAuth)
    // Otherwise the next person on this browser browses anonymously under the
    // previous user's analytics id.
    resetIdentity()
  }, [])

  const session = user
    ? { user: { id: user.uid, email: user.email } }
    : null

  return (
    <AuthContext.Provider value={{ session, loading, signInWithGoogle, sendSignInLink, completeEmailLinkSignIn, signOut }}>
      {children}
    </AuthContext.Provider>
  )
}
